import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bot, Brain, Search, Plus, Star, Filter, GraduationCap, Stethoscope, Wrench, Hammer, User, Code, Mountain } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useSupabaseStore, AgentWithBlueprints } from '@/store/SupabaseStore';
import { useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAppStore } from '@/store';
import { supabase } from '@/integrations/supabase/client';

const AgentCard = ({ 
  id,
  name, 
  description, 
  category, 
  rating, 
  uses, 
  icon: Icon,
  standard = false 
}) => {
  const navigate = useNavigate();
  
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-md text-primary">
              <Icon size={24} />
            </div>
            <CardTitle className="text-lg">{name}</CardTitle>
          </div>
          {standard && (
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-300">
              Standard
            </Badge>
          )}
        </div>
        <CardDescription className="line-clamp-2">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Star size={14} className="fill-yellow-400 text-yellow-400" />
            <span>{rating}/5</span>
          </div>
          <div>
            <span>{uses.toLocaleString()} users</span>
          </div>
          <Badge variant="secondary" className="capitalize">
            {category}
          </Badge>
        </div>
      </CardContent>
      <CardFooter className="border-t pt-4 flex gap-2">
        <Button 
          variant="default" 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"

          onClick={() => navigate(`/agents/use/${id}`)}
        >
          Use Agent
        </Button>
      </CardFooter>
    </Card>
  );
};

const AgentMarketplace = () => {
  const navigate = useNavigate();
  const { agents, fetchAgents } = useSupabaseStore();
  const { toast } = useToast();
  const { user } = useAppStore();

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const standardAgents = useMemo(() => agents.filter(a => a.is_standard), [agents]);

  const handleSeedAgents = async () => {
    if (!user) {
      toast({ title: "Authentication Error", description: "Please log in to seed agents.", variant: "destructive" });
      return;
    }
    toast({ title: "Seeding started...", description: "Populating standard agents and blueprints." });
    
    // Upsert the blueprint
    const { data: bpData, error: bpError } = await supabase
      .from('blueprints')
      .upsert({
        user_id: user.id,
        name: 'Medical Consultant Protocol',
        description: 'A blueprint that instructs an AI to act as a helpful medical consultant, providing general information while including important disclaimers.',
        data: {
          nodes: [
            { id: "start", type: "start", position: { x: 250, y: 50 }, data: { label: "Start" } },
            { id: "system_prompt", type: "system-prompt", position: { x: 250, y: 150 }, data: { label: "System Prompt", content: "You are an AI Medical Consultant. Your role is to provide clear, understandable information about medical conditions, treatments, and general health topics. You must always be empathetic and professional. CRUCIALLY, you must ALWAYS include the following disclaimer at the end of every response: 'I am an AI assistant and not a medical professional. This information is for educational purposes only. Please consult with a qualified healthcare provider for any medical advice, diagnosis, or treatment.' Do not provide diagnoses or prescribe treatments." } },
            { id: "user_input", type: "user-input", position: { x: 250, y: 250 }, data: { label: "User Input" } },
            { id: "ai_response", type: "ai-response", position: { x: 250, y: 350 }, data: { label: "AI Response" } },
            { id: "end", type: "end", position: { x: 250, y: 450 }, data: { label: "End" } }
          ],
          edges: [
            { source: "start", target: "system_prompt", id: "e1" },
            { source: "system_prompt", target: "user_input", id: "e2" },
            { source: "user_input", target: "ai_response", id: "e3" },
            { source: "ai_response", target: "end", id: "e4" }
          ]
        },
        is_private: false,
      }, { onConflict: 'user_id, name' })
      .select('id')
      .single();

    if (bpError) {
      console.error("Error seeding blueprint:", bpError);
      toast({ title: "Error", description: `Failed to seed blueprint: ${bpError.message}`, variant: "destructive" });
      return;
    }
    const medicalBlueprintId = bpData.id;

    const agentsToSeed = [
        { name: 'Medical Consultant', description: 'Specialized in providing medical information, explaining conditions, and offering general health advice. Not a replacement for professional medical care.', instructions: 'Act as a medical consultant. Use the attached blueprint for specific behavior.', domain: 'healthcare', rating: 4.9, uses: 24879, is_standard: true, icon: 'Stethoscope' },
        { name: 'Software Engineer', description: 'Expert in software development, architecture, and debugging across multiple languages and frameworks.', instructions: 'Act as a senior software engineer. Be precise and provide code examples where applicable.', domain: 'technology', rating: 4.8, uses: 35612, is_standard: false, icon: 'Code' },
        { name: 'Mechanical Engineer', description: 'Specializes in mechanical systems, thermodynamics, fluid mechanics, and manufacturing processes.', instructions: 'Act as a knowledgeable mechanical engineer. Explain concepts clearly with diagrams if possible.', domain: 'engineering', rating: 4.6, uses: 18342, is_standard: true, icon: 'Wrench' },
    ];

    const agentsForUpsert = agentsToSeed.map(agent => ({ ...agent, user_id: user.id }));
    
    const { data: agentData, error: agentError } = await supabase
      .from('agents')
      .upsert(agentsForUpsert, { onConflict: 'user_id, name' })
      .select('id, name');

    if (agentError) {
      console.error("Error seeding agents:", agentError);
      toast({ title: "Error", description: `Failed to seed agents: ${agentError.message}`, variant: "destructive" });
      return;
    }

    // Link blueprint to agent
    const medicalAgent = agentData?.find(a => a.name === 'Medical Consultant');
    if (medicalAgent && medicalBlueprintId) {
        await supabase
            .from('agent_blueprints')
            .upsert({
                agent_id: medicalAgent.id,
                blueprint_id: medicalBlueprintId,
            }, { onConflict: 'agent_id, blueprint_id' });
    }

    toast({ title: "Seeding complete!", description: "Standard agents have been added to your account." });
    fetchAgents();
  };

  const iconMap = {
    Stethoscope, Code, Wrench, Hammer, GraduationCap, Mountain, User
  };

  const featuredAgents = standardAgents.sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 6);
  const popularAgents = [...standardAgents].sort((a, b) => (b.uses || 0) - (a.uses || 0));
  const highestRated = [...standardAgents].sort((a, b) => (b.rating || 0) - (a.rating || 0));

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                AI Agent Marketplace
              </h1>
              <p className="text-muted-foreground mt-1">
                Discover specialized AI agents or create your own custom agent
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={handleSeedAgents} variant="outline">Seed Agents</Button>
              <Button
                onClick={() => navigate('/agents/custom')}
                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
              >

                <Plus size={18} />
                Create Custom Agent
              </Button>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative w-full md:w-96">
              <Input
                placeholder="Search agents..."
                className="pl-10"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
            </div>
            
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" className="gap-2">
                <Filter size={16} />
                Filters
              </Button>
            </div>
          </div>
          
          <Tabs defaultValue="featured" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="featured" className="gap-2">
                <Star size={16} />
                Featured
              </TabsTrigger>
              <TabsTrigger value="popular" className="gap-2">
                <User size={16} />
                Most Used
              </TabsTrigger>
              <TabsTrigger value="highest" className="gap-2">
                <Brain size={16} />
                Highest Rated
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="featured" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {featuredAgents.map((agent) => (
                  <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon || 'User']} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="popular" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {popularAgents.map((agent) => (
                  <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon || 'User']} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="highest" className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {highestRated.map((agent) => (
                  <AgentCard key={agent.id} {...agent} category={agent.domain} icon={iconMap[agent.icon || 'User']} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
};

export default AgentMarketplace;